import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Event, EventConfiguration } from '@sm-dashboard/types';
import { useAppContext } from '@/context/context';
import { ExtractEventResponse, useExtractEvent } from '@/api/ai/extractEvent';
import { AddressPickerData } from '../../../Map/AddressMapPicker/AddressMapPicker';
import type { FormValuesProps } from '../EventForm';
import { timeToDate } from '../timeUtils';

const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// TinyMCE normalizes the loaded template to HTML, so raw string comparison against
// config.defaultText fails; compare the visible text instead (tags stripped, entities decoded).
const normalizeHtmlText = (html?: string) =>
  (html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(zwj|zwnj|#8205|#8204);/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

const buildExtractedContent = (fields: ExtractEventResponse) => {
  const parts: string[] = [];
  if (fields.program?.length) {
    parts.push(
      `<p><strong>תוכנית האירוע:</strong></p><ul>${fields.program
        .map(item => `<li>${escapeHtml(item)}</li>`)
        .join('')}</ul>`
    );
  }
  if (fields.prizes?.length) {
    parts.push(`<p><strong>פרסים בהגרלה:</strong> ${fields.prizes.map(escapeHtml).join(', ')}</p>`);
  }
  if (fields.additionalDetails) {
    parts.push(`<p>${escapeHtml(fields.additionalDetails)}</p>`);
  }
  return parts.join('');
};

interface UseAiFillFromAdParams {
  methods: UseFormReturn<FormValuesProps, Event>;
  config: EventConfiguration | undefined;
  content: string | undefined;
  setContent: (content: string) => void;
  locationPicker: AddressPickerData | undefined;
  setLocationPicker: React.Dispatch<React.SetStateAction<AddressPickerData | undefined>>;
  setPlaceholder: (placeholder: string) => void;
}

/**
 * All the "fill the event form from an ad image" logic: extraction request,
 * applying the result to untouched fields only, and the user-confirmation state
 * that gates saving.
 */
export const useAiFillFromAd = ({
  methods,
  config,
  content,
  setContent,
  locationPicker,
  setLocationPicker,
  setPlaceholder
}: UseAiFillFromAdParams) => {
  const { setSnackbar } = useAppContext();
  const [aiFillApplied, setAiFillApplied] = useState(false);
  const [aiConfirmed, setAiConfirmed] = useState(false);
  const { mutate: extractEvent, isPending: isExtracting } = useExtractEvent();
  const {
    formState: { dirtyFields }
  } = methods;

  const isContentUntouched = () => {
    const text = normalizeHtmlText(content);
    return !text || text === normalizeHtmlText(config?.defaultText) || text === 'טקסט חופשי ותמונה';
  };

  const applyExtractedFields = (fields: ExtractEventResponse) => {
    let applied = 0;
    // Fill only fields the user hasn't touched, so manual input is never overwritten.
    const setIfClean = (name: keyof FormValuesProps, value: Date | string) => {
      if (!dirtyFields[name]) {
        methods.setValue(name, value, { shouldDirty: true });
        applied++;
      }
    };
    if (config?.isTitle && fields.title) setIfClean('title', fields.title);
    if (config?.isDescription && fields.description) setIfClean('description', fields.description);
    if (config?.isDate && fields.date) setIfClean('date', new Date(fields.date));
    if (config?.isRangeDate && fields.fromDate) setIfClean('fromDate', new Date(fields.fromDate));
    if (config?.isRangeDate && fields.toDate) setIfClean('toDate', new Date(fields.toDate));
    if (config?.isTime && fields.time) setIfClean('time', timeToDate(fields.time));
    if (config?.isRangeTime && fields.fromTime) setIfClean('fromTime', timeToDate(fields.fromTime));
    if (config?.isRangeTime && fields.toTime) setIfClean('toTime', timeToDate(fields.toTime));
    if (config?.isPhone && fields.phone) setIfClean('phone', fields.phone);
    if (config?.isLink && fields.link) setIfClean('link', fields.link);

    // Free-text editor, only if the user hasn't written there: the AI's own marketing
    // copy of the ad; fall back to a generic program/details block.
    if (config?.isContent && isContentUntouched()) {
      const extractedContent = fields.contentHtml || buildExtractedContent(fields);
      if (extractedContent) {
        setContent(extractedContent);
        applied++;
      }
    }

    if (config?.isAddress && fields.address && !locationPicker) {
      const extractedAddress = fields.address;
      applied++;
      setPlaceholder(extractedAddress);
      new google.maps.Geocoder()
        .geocode({ address: extractedAddress })
        .then(({ results }) => {
          const location = results?.[0]?.geometry?.location;
          if (location) {
            setLocationPicker({ location, address: extractedAddress } as AddressPickerData);
          }
        })
        .catch(() => {
          // Geocoding failed — the user picks the address manually, as today.
        });
    }

    if (applied > 0) {
      setAiFillApplied(true);
      setAiConfirmed(false);
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'פרטי המוקד מולאו אוטומטית מתוך המודעה. נא לבדוק את הפרטים וללחוץ על כפתור השמירה'
      });
    } else {
      setSnackbar({ open: true, severity: 'info', message: 'לא זוהו פרטים חדשים במודעה' });
    }
  };

  const handleImageUploaded = (imageUrl: string, onSuccess?: () => void) => {
    extractEvent(
      { imageUrl },
      {
        onSuccess: fields => {
          applyExtractedFields(fields);
          onSuccess?.();
        },
        onError: () =>
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'התמונה הועלתה, אך לא הצלחנו לחלץ ממנה פרטים. ניתן למלא ידנית'
          })
      }
    );
  };

  const handleFreeText = (freeText: string, onSuccess?: () => void) => {
    extractEvent(
      { freeText },
      {
        onSuccess: fields => {
          applyExtractedFields(fields);
          onSuccess?.();
        },
        onError: () =>
          setSnackbar({
            open: true,
            severity: 'warning',
            message: 'לא הצלחנו לחלץ פרטים מהטקסט. ניתן למלא ידנית'
          })
      }
    );
  };

  return { aiFillApplied, setAiFillApplied, aiConfirmed, setAiConfirmed, isExtracting, handleImageUploaded, handleFreeText };
};
